import {useState, useMemo} from 'react';
import {ToggleGroup, ToggleGroupItem} from '@kroll-resolver-crisp/ui-components-react';
import {ClassificationTreeItem} from './ClassificationTreeItem';
import {TablePanel} from '@/components/ui/table-panel';
import {
  type ClassificationConfig,
  type CreateClassificationConfigRequest,
  type UpdateClassificationConfigRequest
} from '@/api/classificationConfigs';
import {
  useCreateClassificationConfig,
  useUpdateClassificationConfig,
  useDeleteClassificationConfig,
  useSetClassificationHidden
} from '@/api/hooks';

type Props = {
  readonly initialConfigs: ClassificationConfig[];
};

const CATEGORIES = ['Content', 'Actor'] as const;

// The full taxonomy editor - admin only, reached via the Classifications tab on /admin.
// analyst_admin gets the toggle-only ClassificationVisibilityAdmin at /admin instead.
export function ClassificationsAdmin({initialConfigs}: Props) {
  const [configs, setConfigs] = useState<ClassificationConfig[]>(initialConfigs);
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>('Content');

  const createMut = useCreateClassificationConfig();
  const updateMut = useUpdateClassificationConfig();
  const deleteMut = useDeleteClassificationConfig();
  const hiddenMut = useSetClassificationHidden();

  // Build tree structure: verticals and risks are both filtered to the active taxonomy, so an Actor
  // tab never shows Content-only rows and vice versa. A row carrying no category belongs to both
  // tabs rather than to neither.
  const tree = useMemo(() => {
    const rootItems = configs
      .filter((c) => c.parent_id === null && c.categories.includes(category))
      .sort((a, b) => a.sort_order - b.sort_order);
    const childrenMap = new Map<string, ClassificationConfig[]>();

    // Build map of parent_id -> children
    for (const config of configs) {
      if (config.parent_id && config.categories.includes(category)) {
        if (!childrenMap.has(config.parent_id)) {
          childrenMap.set(config.parent_id, []);
        }

        childrenMap.get(config.parent_id)!.push(config);
      }
    }

    // Sort children by sort_order
    for (const children of childrenMap.values()) {
      children.sort((a, b) => a.sort_order - b.sort_order);
    }

    return {rootItems, childrenMap};
  }, [configs, category]);

  // Recursive function to get all children of an item
  const getChildren = (itemId: string): ClassificationConfig[] =>
    tree.childrenMap.get(itemId) ?? [];

  const handleCreate = async (data: CreateClassificationConfigRequest) => {
    const created = await createMut.mutateAsync(data);
    setConfigs((prev) => [...prev, created]);
    return created;
  };

  const handleUpdate = async (id: string, data: UpdateClassificationConfigRequest) => {
    const updated = await updateMut.mutateAsync({id, body: data});
    setConfigs((prev) => prev.map((c) => (c.id === id ? updated : c)));
  };

  const handleToggleHidden = async (id: string, hidden: boolean) => {
    // Hiding a policy violation cascades to its risks server-side, so more than one row can come
    // back - merge every one of them into state, not just the row that was clicked.
    const updated = await hiddenMut.mutateAsync({id, facet: category, hidden});
    const byId = new Map(updated.map((c) => [c.id, c]));
    setConfigs((prev) => prev.map((c) => byId.get(c.id) ?? c));
  };

  const handleDelete = async (id: string) => {
    await deleteMut.mutateAsync(id);
    // Remove item and all its descendants from state
    const toRemove = new Set<string>();
    const findDescendants = (itemId: string) => {
      toRemove.add(itemId);
      const children = getChildren(itemId);
      for (const child of children) {
        findDescendants(child.id);
      }
    };

    findDescendants(id);
    setConfigs((prev) => prev.filter((c) => !toRemove.has(c.id)));
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Category tabs: Actor vs Content taxonomy, sharing the same verticals */}
      <ToggleGroup
        variant="outline"
        spacing={0}
        size="sm"
        aria-label="Taxonomy"
        value={[category]}
        onValueChange={(next) => {
          const [selected] = next as Array<(typeof CATEGORIES)[number]>;
          if (selected) {
            setCategory(selected);
          }
        }}>
        {CATEGORIES.map((c) => (
          <ToggleGroupItem key={c} value={c}>
            {c} Classifications
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {/* Classification Tree */}
      {configs.length > 0 && (
        <TablePanel>
          {tree.rootItems.map((item) => (
            <ClassificationTreeItem
              key={item.id}
              item={item}
              allConfigs={configs}
              level={0}
              category={category}
              onUpdate={handleUpdate}
              onCreate={handleCreate}
              onDelete={handleDelete}
              onToggleHidden={handleToggleHidden}
            />
          ))}
        </TablePanel>
      )}
    </div>
  );
}
